import { useEffect, useRef } from 'react';
import { io, Socket } from 'socket.io-client';

let socket: Socket | null = null;

/**
 * Returns the singleton authenticated socket. Cookie auth — the
 * httpOnly `keenez_session` cookie is sent on the handshake, the
 * backend gateway verifies it.
 */
export function getSocket(): Socket {
  if (!socket) {
    const baseUrl = (import.meta.env.VITE_API_URL as string | undefined) || window.location.origin;
    socket = io(baseUrl, {
      withCredentials: true,
      transports: ['websocket', 'polling'],
      autoConnect: true,
      reconnection: true,
      reconnectionDelay: 1500,
    });
  }
  return socket;
}

/**
 * React hook — subscribes to a socket event for the lifetime of the
 * component. Cleans up on unmount; survives reconnects automatically
 * because we re-attach on every render.
 */
export function useSocketEvent<T = unknown>(event: string, handler: (data: T) => void): void {
  // Keep a ref so the listener identity is stable across re-renders.
  const ref = useRef(handler);
  ref.current = handler;
  useEffect(() => {
    const s = getSocket();
    const fn = (data: T) => ref.current(data);
    s.on(event, fn);
    return () => { s.off(event, fn); };
  }, [event]);
}
